import type { NamespaceCatalog } from '@/i18n/ns';

const companyProcessingCopy = {
  en: {
    title: 'AI processing',
    intro:
      'Choose where Poneva’s AI runs for everyone at {company}: chat, voice, meetings and agents.',
    loading: 'Loading AI processing…',
    loadError: 'We couldn’t load AI processing. Check your connection and try again.',
    retry: 'Try again',
    nowEuTitle: 'AI runs in the EU',
    nowEuBody:
      'New chat, voice, meeting and agent requests use EU routes and never fall back outside the EU.',
    nowGlobalTitle: 'Using the best models worldwide',
    nowGlobalBody:
      'Auto chat runs in the EU. Other model choices and voices may run outside the EU.',
    nowScope: 'Connected services, websites and your own agents follow their own rules.',
    choicesLabel: 'Where AI runs',
    euTitle: 'Keep all AI processing in the EU',
    euBody: 'For strict data rules. A few newer models and voices aren’t available in the EU yet.',
    globalTitle: 'Use the best models worldwide',
    globalBody: 'The strongest models and voices, processed in the EU or elsewhere.',
    current: 'Current',
    detailsToggle: 'What changes',
    detailsModel:
      'Auto uses {model} in the EU. If it is unavailable, {fallback} answers in the EU.',
    detailsVoice: 'EU voices can sound different and respond a little slower.',
    detailsMeetings:
      'Meeting repair needs a second EU speech engine and pauses when none is available.',
    detailsCompaction: 'Some newer models can’t shorten long chats in the EU yet.',
    detailsOthers:
      'Integrations, public websites, your own agents, and account or billing services follow their own provider’s rules.',
    detailsNext:
      'A change applies to new requests. Requests already running finish as they started.',
    decisionToEu: 'Everyone at {company} will keep AI processing in the EU.',
    consequenceToEu: 'A few newer models and voices won’t be available.',
    decisionToGlobal: 'Everyone at {company} will use the best models worldwide.',
    consequenceToGlobal: 'New requests may be processed outside the EU.',
    save: 'Save',
    saving: 'Saving…',
    saved: 'Saved',
    adminOnly: 'Only Owners and Admins can change this.',
    adminOnlyAsk:
      'Only Owners and Admins can change this. Ask {owner} if {company} needs a different choice.',
    currentChoice: 'Current choice',
    conflict: 'Someone changed this a moment ago. We’ve loaded the latest choice.',
    saveError: 'We couldn’t save this. Try again.',
  },
  bg: {
    title: 'Обработка от AI',
    intro: 'Изберете къде работи AI на Poneva за всички в {company}: чат, глас, срещи и агенти.',
    loading: 'Зареждане на обработката от AI…',
    loadError:
      'Не успяхме да заредим настройката за обработка от AI. Проверете връзката си и опитайте отново.',
    retry: 'Опитайте отново',
    nowEuTitle: 'AI работи в ЕС',
    nowEuBody:
      'Новите заявки за чат, глас, срещи и агенти използват маршрути в ЕС и никога не се пренасочват извън ЕС.',
    nowGlobalTitle: 'Използване на най-добрите модели в целия свят',
    nowGlobalBody:
      'Автоматичният чат работи в ЕС. Другите избори на модели и гласове може да се обработват извън ЕС.',
    nowScope:
      'Свързаните услуги, уебсайтовете и вашите собствени агенти следват собствените си правила.',
    choicesLabel: 'Къде работи AI',
    euTitle: 'Цялата обработка от AI да остане в ЕС',
    euBody:
      'За строги правила за данните. Някои по-нови модели и гласове все още не са налични в ЕС.',
    globalTitle: 'Използване на най-добрите модели в целия свят',
    globalBody: 'Най-мощните модели и гласове се обработват в ЕС или на друго място.',
    current: 'Текущо',
    detailsToggle: 'Какво се променя',
    detailsModel:
      'Автоматичният режим използва {model} в ЕС. Ако не е наличен, {fallback} отговаря в ЕС.',
    detailsVoice: 'Гласовете от ЕС може да звучат различно и да отговарят малко по-бавно.',
    detailsMeetings:
      'Възстановяването на срещи изисква втори двигател за реч в ЕС и се поставя на пауза, когато такъв не е наличен.',
    detailsCompaction: 'Някои по-нови модели все още не могат да съкращават дълги чатове в ЕС.',
    detailsOthers:
      'Интеграциите, публичните уебсайтове, вашите собствени агенти и услугите за акаунти или таксуване следват правилата на съответния си доставчик.',
    detailsNext:
      'Промяната важи за новите заявки. Заявките, които вече се обработват, ще приключат по започнатия начин.',
    decisionToEu: 'Всички в {company} ще запазят обработката от AI в ЕС.',
    consequenceToEu: 'Някои по-нови модели и гласове няма да са налични.',
    decisionToGlobal: 'Всички в {company} ще използват най-добрите модели в целия свят.',
    consequenceToGlobal: 'Новите заявки може да се обработват извън ЕС.',
    save: 'Запазване',
    saving: 'Запазване…',
    saved: 'Запазено',
    adminOnly: 'Само собствениците и администраторите могат да променят това.',
    adminOnlyAsk:
      'Само собствениците и администраторите могат да променят това. Попитайте {owner}, ако {company} се нуждае от друг избор.',
    currentChoice: 'Текущ избор',
    conflict: 'Някой промени това преди малко. Заредихме най-новия избор.',
    saveError: 'Не успяхме да запазим това. Опитайте отново.',
  },
  hr: {
    title: 'AI obrada',
    intro:
      'Odaberite gdje se Ponevina umjetna inteligencija pokreće za sve u tvrtki {company}: chat, glasovne poruke, sastanci i agenti.',
    loading: 'Učitavanje AI obrade…',
    loadError: 'Nismo mogli učitati AI obradu. Provjerite svoju vezu i pokušajte ponovno.',
    retry: 'Pokušaj ponovno',
    nowEuTitle: 'AI se pokreće u EU-u',
    nowEuBody:
      'Novi zahtjevi za chat, glasovne poruke, sastanke i agente koriste rute u EU-u i nikad se ne preusmjeravaju izvan EU-a.',
    nowGlobalTitle: 'Upotreba najboljih modela diljem svijeta',
    nowGlobalBody:
      'Automatski chat pokreće se u EU-u. Ostali odabiri modela i glasovi mogu se pokretati izvan EU-a.',
    nowScope: 'Povezane usluge, web-mjesta i vaši agenti slijede vlastita pravila.',
    choicesLabel: 'Gdje se AI pokreće',
    euTitle: 'Zadrži svu AI obradu u EU-u',
    euBody:
      'Za stroga pravila o podacima. Nekoliko novijih modela i glasova još nije dostupno u EU-u.',
    globalTitle: 'Upotrijebi najbolje modele diljem svijeta',
    globalBody: 'Najmoćniji modeli i glasovi obrađuju se u EU-u ili drugdje.',
    current: 'Trenutačno',
    detailsToggle: 'Što se mijenja',
    detailsModel:
      'Automatski način upotrebljava {model} u EU-u. Ako nije dostupan, {fallback} odgovara u EU-u.',
    detailsVoice: 'Glasovi iz EU-a mogu zvučati drugačije i odgovarati malo sporije.',
    detailsMeetings:
      'Popravak sastanaka zahtijeva drugi mehanizam za govor u EU-u i pauzira kada nijedan nije dostupan.',
    detailsCompaction: 'Neki noviji modeli još ne mogu skratiti duge chatove u EU-u.',
    detailsOthers:
      'Integracije, javna web-mjesta, vaši agenti te usluge računa ili naplate slijede pravila vlastitog pružatelja usluge.',
    detailsNext:
      'Promjena se primjenjuje na nove zahtjeve. Zahtjevi koji su već u tijeku dovršavaju se na započeti način.',
    decisionToEu: 'Svi u tvrtki {company} zadržat će AI obradu u EU-u.',
    consequenceToEu: 'Nekoliko novijih modela i glasova neće biti dostupno.',
    decisionToGlobal: 'Svi u tvrtki {company} upotrebljavat će najbolje modele diljem svijeta.',
    consequenceToGlobal: 'Novi se zahtjevi mogu obrađivati izvan EU-a.',
    save: 'Spremi',
    saving: 'Spremanje…',
    saved: 'Spremljeno',
    adminOnly: 'Samo vlasnici i administratori mogu ovo promijeniti.',
    adminOnlyAsk:
      'Samo vlasnici i administratori mogu ovo promijeniti. Pitajte {owner} ako je tvrtki {company} potreban drugačiji odabir.',
    currentChoice: 'Trenutačni odabir',
    conflict: 'Netko je ovo promijenio prije trenutak. Učitali smo najnoviji odabir.',
    saveError: 'Nismo mogli spremiti ovo. Pokušajte ponovno.',
  },
  cs: {
    title: 'Zpracování AI',
    intro: 'Zvolte, kde běží AI Ponevy pro všechny ve {company}: chat, hlas, schůzky a agenti.',
    loading: 'Načítání zpracování AI…',
    loadError: 'Zpracování AI se nepodařilo načíst. Zkontrolujte připojení a zkuste to znovu.',
    retry: 'Zkusit znovu',
    nowEuTitle: 'AI běží v EU',
    nowEuBody:
      'Nové požadavky v chatu, hlasové komunikaci, schůzkách a agentech využívají trasy v EU a nikdy nejsou přesměrovány mimo EU.',
    nowGlobalTitle: 'Používání nejlepších modelů po celém světě',
    nowGlobalBody: 'Automatický chat běží v EU. Jiné volby modelů a hlasy mohou běžet mimo EU.',
    nowScope: 'Připojené služby, weby a vaši vlastní agenti se řídí vlastními pravidly.',
    choicesLabel: 'Kde běží AI',
    euTitle: 'Zachovat veškeré zpracování AI v EU',
    euBody:
      'Pro přísná pravidla ochrany dat. Několik novějších modelů a hlasů zatím není v EU k dispozici.',
    globalTitle: 'Používat nejlepší modely po celém světě',
    globalBody: 'Nejvýkonnější modely a hlasy zpracovávané v EU i jinde.',
    current: 'Aktuální',
    detailsToggle: 'Co se změní',
    detailsModel:
      'Automatický režim používá v EU {model}. Pokud není k dispozici, odpovídá v EU {fallback}.',
    detailsVoice: 'Hlasy v EU mohou znít jinak a reagovat o něco pomaleji.',
    detailsMeetings:
      'Oprava schůzek vyžaduje druhý hlasový engine v EU a pozastaví se, pokud není k dispozici žádný.',
    detailsCompaction: 'Některé novější modely zatím v EU nedokážou zkracovat dlouhé chaty.',
    detailsOthers:
      'Integrace, veřejné weby, vaši vlastní agenti a služby účtu nebo fakturace se řídí pravidly vlastního poskytovatele.',
    detailsNext:
      'Změna se použije na nové požadavky. Požadavky, které už běží, se dokončí podle původního nastavení.',
    decisionToEu: 'Všichni ve {company} zachovají zpracování AI v EU.',
    consequenceToEu: 'Některé novější modely a hlasy nebudou k dispozici.',
    decisionToGlobal: 'Všichni ve {company} budou používat nejlepší modely po celém světě.',
    consequenceToGlobal: 'Nové požadavky mohou být zpracovávány mimo EU.',
    save: 'Uložit',
    saving: 'Ukládání…',
    saved: 'Uloženo',
    adminOnly: 'Toto mohou změnit pouze vlastníci a administrátoři.',
    adminOnlyAsk:
      'Toto mohou změnit pouze vlastníci a administrátoři. Pokud {company} potřebuje jinou volbu, zeptejte se {owner}.',
    currentChoice: 'Aktuální volba',
    conflict: 'Někdo tuto volbu před chvílí změnil. Načetli jsme nejnovější volbu.',
    saveError: 'Toto se nepodařilo uložit. Zkuste to znovu.',
  },
  da: {
    title: 'AI-behandling',
    intro: 'Vælg, hvor Ponevas AI kører for alle i {company}: chat, tale, møder og agenter.',
    loading: 'Indlæser AI-behandling…',
    loadError: 'Vi kunne ikke indlæse AI-behandlingen. Kontrollér din forbindelse, og prøv igen.',
    retry: 'Prøv igen',
    nowEuTitle: 'AI kører i EU',
    nowEuBody:
      'Nye chat-, tale-, møde- og agentanmodninger bruger EU-ruter og falder aldrig tilbage til ruter uden for EU.',
    nowGlobalTitle: 'Bruger de bedste modeller verden over',
    nowGlobalBody: 'Automatisk chat kører i EU. Andre modelvalg og stemmer kan køre uden for EU.',
    nowScope: 'Forbundne tjenester, websteder og dine egne agenter følger deres egne regler.',
    choicesLabel: 'Hvor AI kører',
    euTitle: 'Behold al AI-behandling i EU',
    euBody:
      'Til strenge datakrav. Nogle få nyere modeller og stemmer er endnu ikke tilgængelige i EU.',
    globalTitle: 'Brug de bedste modeller verden over',
    globalBody: 'De stærkeste modeller og stemmer behandles i EU eller andre steder.',
    current: 'Nuværende',
    detailsToggle: 'Hvad ændres',
    detailsModel:
      'Automatisk bruger {model} i EU. Hvis den ikke er tilgængelig, svarer {fallback} i EU.',
    detailsVoice: 'EU-stemmer kan lyde anderledes og svare lidt langsommere.',
    detailsMeetings:
      'Reparation af møder kræver en ekstra EU-talemotor og sættes på pause, når ingen er tilgængelig.',
    detailsCompaction: 'Nogle nyere modeller kan endnu ikke forkorte lange chats i EU.',
    detailsOthers:
      'Integrationer, offentlige websteder, dine egne agenter samt konto- og betalingstjenester følger deres egen udbyders regler.',
    detailsNext:
      'En ændring gælder for nye anmodninger. Anmodninger, der allerede kører, afsluttes, som de blev startet.',
    decisionToEu: 'Alle i {company} beholder AI-behandlingen i EU.',
    consequenceToEu: 'Nogle få nyere modeller og stemmer vil ikke være tilgængelige.',
    decisionToGlobal: 'Alle i {company} bruger de bedste modeller verden over.',
    consequenceToGlobal: 'Nye anmodninger kan blive behandlet uden for EU.',
    save: 'Gem',
    saving: 'Gemmer…',
    saved: 'Gemt',
    adminOnly: 'Kun ejere og administratorer kan ændre dette.',
    adminOnlyAsk:
      'Kun ejere og administratorer kan ændre dette. Spørg {owner}, hvis {company} har brug for et andet valg.',
    currentChoice: 'Nuværende valg',
    conflict: 'En anden ændrede dette for et øjeblik siden. Vi har indlæst det seneste valg.',
    saveError: 'Vi kunne ikke gemme dette. Prøv igen.',
  },
  nl: {
    title: 'AI-verwerking',
    intro:
      'Kies waar de AI van Poneva draait voor iedereen bij {company}: chat, spraak, vergaderingen en agents.',
    loading: 'AI-verwerking wordt geladen…',
    loadError:
      'We konden de AI-verwerking niet laden. Controleer je verbinding en probeer het opnieuw.',
    retry: 'Opnieuw proberen',
    nowEuTitle: 'AI draait in de EU',
    nowEuBody:
      'Nieuwe chat-, spraak-, vergaderings- en agentverzoeken gebruiken EU-routes en vallen nooit terug op routes buiten de EU.',
    nowGlobalTitle: 'De beste modellen wereldwijd gebruiken',
    nowGlobalBody:
      'Automatische chat draait in de EU. Andere modelkeuzes en stemmen kunnen buiten de EU draaien.',
    nowScope: 'Verbonden services, websites en je eigen agents volgen hun eigen regels.',
    choicesLabel: 'Waar AI draait',
    euTitle: 'Alle AI-verwerking in de EU houden',
    euBody:
      'Voor strenge regels rond gegevens. Enkele nieuwere modellen en stemmen zijn nog niet beschikbaar in de EU.',
    globalTitle: 'De beste modellen wereldwijd gebruiken',
    globalBody: 'De krachtigste modellen en stemmen, verwerkt in de EU of elders.',
    current: 'Huidige keuze',
    detailsToggle: 'Wat verandert er',
    detailsModel:
      'Automatisch gebruikt {model} in de EU. Als dit niet beschikbaar is, antwoordt {fallback} in de EU.',
    detailsVoice: 'EU-stemmen kunnen anders klinken en iets trager reageren.',
    detailsMeetings:
      'Herstel van vergaderingen vereist een tweede EU-spraakengine en wordt onderbroken als geen engine beschikbaar is.',
    detailsCompaction: 'Sommige nieuwere modellen kunnen lange chats in de EU nog niet inkorten.',
    detailsOthers:
      'Integraties, openbare websites, je eigen agents en account- of factureringsservices volgen de regels van hun eigen provider.',
    detailsNext:
      'Een wijziging geldt voor nieuwe verzoeken. Verzoeken die al worden uitgevoerd, worden voltooid zoals ze zijn gestart.',
    decisionToEu: 'Iedereen bij {company} houdt AI-verwerking in de EU.',
    consequenceToEu: 'Enkele nieuwere modellen en stemmen zijn niet beschikbaar.',
    decisionToGlobal: 'Iedereen bij {company} gebruikt de beste modellen wereldwijd.',
    consequenceToGlobal: 'Nieuwe verzoeken kunnen buiten de EU worden verwerkt.',
    save: 'Opslaan',
    saving: 'Opslaan…',
    saved: 'Opgeslagen',
    adminOnly: 'Alleen eigenaren en beheerders kunnen dit wijzigen.',
    adminOnlyAsk:
      'Alleen eigenaren en beheerders kunnen dit wijzigen. Vraag {owner} of {company} een andere keuze nodig heeft.',
    currentChoice: 'Huidige keuze',
    conflict: 'Iemand heeft dit zojuist gewijzigd. We hebben de nieuwste keuze geladen.',
    saveError: 'We konden dit niet opslaan. Probeer het opnieuw.',
  },
  et: {
    title: 'AI töötlemine',
    intro:
      'Vali, kus Poneva AI töötab kõigi jaoks ettevõttes {company}: vestlused, hääl, koosolekud ja agendid.',
    loading: 'AI töötlemise laadimine…',
    loadError: 'AI töötlemist ei õnnestunud laadida. Kontrolli ühendust ja proovi uuesti.',
    retry: 'Proovi uuesti',
    nowEuTitle: 'AI töötab EL-is',
    nowEuBody:
      'Uued vestlus-, hääle-, koosoleku- ja agendipäringud kasutavad EL-i marsruute ega lähe kunagi EL-ist välja.',
    nowGlobalTitle: 'Kasutusel on parimad mudelid kogu maailmast',
    nowGlobalBody:
      'Automaatne vestlus töötab EL-is. Muud mudelivalikud ja hääled võivad töötada väljaspool EL-i.',
    nowScope: 'Ühendatud teenused, veebisaidid ja sinu enda agendid järgivad oma reegleid.',
    choicesLabel: 'Kus AI töötab',
    euTitle: 'Hoia kogu AI töötlemine EL-is',
    euBody: 'Rangete andmereeglite jaoks. Mõned uuemad mudelid ja hääled pole EL-is veel saadaval.',
    globalTitle: 'Kasuta parimaid mudeleid kogu maailmast',
    globalBody: 'Kõige võimsamad mudelid ja hääled, mida töödeldakse EL-is või mujal.',
    current: 'Praegune',
    detailsToggle: 'Mis muutub',
    detailsModel:
      'Automaatne valik kasutab EL-is mudelit {model}. Kui see pole saadaval, vastab EL-is {fallback}.',
    detailsVoice: 'EL-i hääled võivad kõlada erinevalt ja vastata veidi aeglasemalt.',
    detailsMeetings:
      'Koosoleku heli parandamiseks on vaja teist EL-i kõnemootorit ning funktsioon peatub, kui seda pole saadaval.',
    detailsCompaction: 'Mõned uuemad mudelid ei saa veel EL-is pikki vestlusi lühendada.',
    detailsOthers:
      'Integratsioonid, avalikud veebisaidid, sinu enda agendid ning konto- või arveldusteenused järgivad oma teenusepakkuja reegleid.',
    detailsNext:
      'Muudatus rakendub uutele päringutele. Juba töötavad päringud lõpetatakse seniste seadete järgi.',
    decisionToEu: 'Kõigi ettevõtte {company} liikmete AI töötlemine jääb EL-i.',
    consequenceToEu: 'Mõned uuemad mudelid ja hääled pole saadaval.',
    decisionToGlobal:
      'Kõik ettevõtte {company} liikmed kasutavad parimaid mudeleid kogu maailmast.',
    consequenceToGlobal: 'Uusi päringuid võidakse töödelda väljaspool EL-i.',
    save: 'Salvesta',
    saving: 'Salvestamine…',
    saved: 'Salvestatud',
    adminOnly: 'Ainult omanikud ja administraatorid saavad seda muuta.',
    adminOnlyAsk:
      'Ainult omanikud ja administraatorid saavad seda muuta. Kui ettevõte {company} vajab teistsugust valikut, küsi {owner} käest.',
    currentChoice: 'Praegune valik',
    conflict: 'Keegi muutis seda hetk tagasi. Laadisime uusima valiku.',
    saveError: 'Seda ei õnnestunud salvestada. Proovi uuesti.',
  },
  fi: {
    title: 'Tekoälyn käsittely',
    intro:
      'Valitse, missä Ponevan tekoäly toimii kaikille yrityksessä {company}: chatissa, puheessa, kokouksissa ja agenteissa.',
    loading: 'Tekoälyn käsittelyä ladataan…',
    loadError: 'Tekoälyn käsittelyä ei voitu ladata. Tarkista yhteytesi ja yritä uudelleen.',
    retry: 'Yritä uudelleen',
    nowEuTitle: 'Tekoäly toimii EU:ssa',
    nowEuBody:
      'Uudet chat-, puhe-, kokous- ja agenttipyynnöt käyttävät EU-reittejä eivätkä koskaan siirry EU:n ulkopuolelle.',
    nowGlobalTitle: 'Parhaat mallit käytössä maailmanlaajuisesti',
    nowGlobalBody:
      'Automaattinen chat toimii EU:ssa. Muut mallivalinnat ja äänet voivat toimia EU:n ulkopuolella.',
    nowScope: 'Yhdistetyt palvelut, verkkosivustot ja omat agenttisi noudattavat omia sääntöjään.',
    choicesLabel: 'Missä tekoäly toimii',
    euTitle: 'Pidä kaikki tekoälyn käsittely EU:ssa',
    euBody:
      'Tiukkoja tietosuojasääntöjä varten. Muutamat uudemmat mallit ja äänet eivät ole vielä saatavilla EU:ssa.',
    globalTitle: 'Käytä parhaita malleja maailmanlaajuisesti',
    globalBody: 'Tehokkaimmat mallit ja äänet käsitellään EU:ssa tai muualla.',
    current: 'Nykyinen',
    detailsToggle: 'Mikä muuttuu',
    detailsModel:
      'Automaattinen toiminto käyttää mallia {model} EU:ssa. Jos se ei ole saatavilla, {fallback} vastaa EU:ssa.',
    detailsVoice: 'EU:n äänet voivat kuulostaa erilaisilta ja vastata hieman hitaammin.',
    detailsMeetings:
      'Kokousten korjaus tarvitsee toisen EU:ssa toimivan puhemoottorin ja keskeytyy, jos sellaista ei ole saatavilla.',
    detailsCompaction:
      'Jotkin uudemmat mallit eivät vielä pysty lyhentämään pitkiä chatteja EU:ssa.',
    detailsOthers:
      'Integraatiot, julkiset verkkosivustot, omat agenttisi sekä tili- ja laskutuspalvelut noudattavat omien palveluntarjoajiensa sääntöjä.',
    detailsNext:
      'Muutos koskee uusia pyyntöjä. Jo käynnissä olevat pyynnöt suoritetaan alkuperäisten asetusten mukaisesti.',
    decisionToEu: 'Kaikille yrityksen {company} käyttäjille tekoälyn käsittely säilyy EU:ssa.',
    consequenceToEu: 'Muutamat uudemmat mallit ja äänet eivät ole saatavilla.',
    decisionToGlobal:
      'Kaikki yrityksen {company} käyttäjät käyttävät parhaita malleja maailmanlaajuisesti.',
    consequenceToGlobal: 'Uudet pyynnöt voidaan käsitellä EU:n ulkopuolella.',
    save: 'Tallenna',
    saving: 'Tallennetaan…',
    saved: 'Tallennettu',
    adminOnly: 'Vain omistajat ja ylläpitäjät voivat muuttaa tätä.',
    adminOnlyAsk:
      'Vain omistajat ja ylläpitäjät voivat muuttaa tätä. Kysy käyttäjältä {owner}, jos yritys {company} tarvitsee toisen valinnan.',
    currentChoice: 'Nykyinen valinta',
    conflict: 'Joku muutti tätä hetki sitten. Latasimme uusimman valinnan.',
    saveError: 'Tätä ei voitu tallentaa. Yritä uudelleen.',
  },
  fr: {
    title: 'Traitement de l’IA',
    intro:
      'Choisissez où l’IA de Poneva s’exécute pour tout le monde chez {company} : chat, appels vocaux, réunions et agents.',
    loading: 'Chargement du traitement de l’IA…',
    loadError:
      'Impossible de charger le traitement de l’IA. Vérifiez votre connexion et réessayez.',
    retry: 'Réessayer',
    nowEuTitle: 'L’IA s’exécute dans l’UE',
    nowEuBody:
      'Les nouvelles demandes de chat, d’appel vocal, de réunion et d’agent utilisent des routes dans l’UE et ne sont jamais redirigées en dehors de l’UE.',
    nowGlobalTitle: 'Utilisation des meilleurs modèles dans le monde entier',
    nowGlobalBody:
      'Le chat automatique s’exécute dans l’UE. D’autres choix de modèles et de voix peuvent s’exécuter en dehors de l’UE.',
    nowScope:
      'Les services connectés, les sites web et vos propres agents suivent leurs propres règles.',
    choicesLabel: 'Lieu d’exécution de l’IA',
    euTitle: 'Conserver tout le traitement de l’IA dans l’UE',
    euBody:
      'Pour respecter des règles strictes en matière de données. Quelques modèles et voix plus récents ne sont pas encore disponibles dans l’UE.',
    globalTitle: 'Utiliser les meilleurs modèles dans le monde entier',
    globalBody: 'Les modèles et voix les plus performants, traités dans l’UE ou ailleurs.',
    current: 'Actuel',
    detailsToggle: 'Ce qui change',
    detailsModel:
      'Le mode automatique utilise {model} dans l’UE. S’il n’est pas disponible, {fallback} répond dans l’UE.',
    detailsVoice:
      'Les voix de l’UE peuvent avoir un rendu différent et répondre un peu plus lentement.',
    detailsMeetings:
      'La réparation des réunions nécessite un deuxième moteur vocal dans l’UE et s’interrompt lorsqu’aucun moteur n’est disponible.',
    detailsCompaction:
      'Certains modèles plus récents ne peuvent pas encore raccourcir les longs chats dans l’UE.',
    detailsOthers:
      'Les intégrations, les sites web publics, vos propres agents et les services de compte ou de facturation suivent les règles de leurs propres fournisseurs.',
    detailsNext:
      'Une modification s’applique aux nouvelles demandes. Les demandes déjà en cours se terminent comme elles ont commencé.',
    decisionToEu: 'Tout le monde chez {company} conservera le traitement de l’IA dans l’UE.',
    consequenceToEu: 'Quelques modèles et voix plus récents ne seront pas disponibles.',
    decisionToGlobal:
      'Tout le monde chez {company} utilisera les meilleurs modèles dans le monde entier.',
    consequenceToGlobal: 'Les nouvelles demandes pourront être traitées en dehors de l’UE.',
    save: 'Enregistrer',
    saving: 'Enregistrement…',
    saved: 'Enregistré',
    adminOnly: 'Seuls les propriétaires et les administrateurs peuvent modifier ce paramètre.',
    adminOnlyAsk:
      'Seuls les propriétaires et les administrateurs peuvent modifier ce paramètre. Demandez à {owner} si {company} doit utiliser un autre choix.',
    currentChoice: 'Choix actuel',
    conflict:
      'Quelqu’un a modifié ce paramètre à l’instant. Nous avons chargé le choix le plus récent.',
    saveError: 'Impossible d’enregistrer cette modification. Réessayez.',
  },
  de: {
    title: 'KI-Verarbeitung',
    intro:
      'Wählen Sie, wo die KI von Poneva für alle bei {company} ausgeführt wird: Chat, Sprache, Meetings und Agents.',
    loading: 'KI-Verarbeitung wird geladen…',
    loadError:
      'Die KI-Verarbeitung konnte nicht geladen werden. Überprüfen Sie Ihre Verbindung und versuchen Sie es erneut.',
    retry: 'Erneut versuchen',
    nowEuTitle: 'KI wird in der EU ausgeführt',
    nowEuBody:
      'Neue Chat-, Sprach-, Meeting- und Agent-Anfragen nutzen EU-Routen und werden niemals außerhalb der EU verarbeitet.',
    nowGlobalTitle: 'Die besten Modelle weltweit nutzen',
    nowGlobalBody:
      'Der automatische Chat wird in der EU ausgeführt. Andere Modellauswahlen und Stimmen können außerhalb der EU verarbeitet werden.',
    nowScope: 'Verbundene Dienste, Websites und Ihre eigenen Agents folgen ihren eigenen Regeln.',
    choicesLabel: 'Wo die KI ausgeführt wird',
    euTitle: 'Die gesamte KI-Verarbeitung in der EU behalten',
    euBody:
      'Für strenge Datenschutzvorgaben. Einige neuere Modelle und Stimmen sind in der EU noch nicht verfügbar.',
    globalTitle: 'Die besten Modelle weltweit nutzen',
    globalBody:
      'Die leistungsstärksten Modelle und Stimmen werden in der EU oder anderswo verarbeitet.',
    current: 'Aktuell',
    detailsToggle: 'Was sich ändert',
    detailsModel:
      'Auto verwendet {model} in der EU. Falls das Modell nicht verfügbar ist, antwortet {fallback} in der EU.',
    detailsVoice: 'EU-Stimmen können anders klingen und etwas langsamer antworten.',
    detailsMeetings:
      'Die Reparatur von Meetings benötigt eine zweite EU-Sprachengine und pausiert, wenn keine verfügbar ist.',
    detailsCompaction: 'Einige neuere Modelle können lange Chats in der EU noch nicht verkürzen.',
    detailsOthers:
      'Integrationen, öffentliche Websites, Ihre eigenen Agents sowie Konto- und Abrechnungsdienste folgen den Regeln ihres jeweiligen Anbieters.',
    detailsNext:
      'Eine Änderung gilt für neue Anfragen. Bereits laufende Anfragen werden wie begonnen abgeschlossen.',
    decisionToEu: 'Alle bei {company} behalten die KI-Verarbeitung in der EU bei.',
    consequenceToEu: 'Einige neuere Modelle und Stimmen werden nicht verfügbar sein.',
    decisionToGlobal: 'Alle bei {company} nutzen die besten Modelle weltweit.',
    consequenceToGlobal: 'Neue Anfragen können außerhalb der EU verarbeitet werden.',
    save: 'Speichern',
    saving: 'Wird gespeichert…',
    saved: 'Gespeichert',
    adminOnly: 'Nur Inhaber und Admins können dies ändern.',
    adminOnlyAsk:
      'Nur Inhaber und Admins können dies ändern. Frage {owner}, wenn {company} eine andere Auswahl benötigt.',
    currentChoice: 'Aktuelle Auswahl',
    conflict: 'Jemand hat dies gerade geändert. Wir haben die neueste Auswahl geladen.',
    saveError: 'Dies konnte nicht gespeichert werden. Versuchen Sie es erneut.',
  },
  el: {
    title: 'Επεξεργασία AI',
    intro:
      'Επιλέξτε πού εκτελείται το AI της Poneva για όλους στην εταιρεία {company}: συνομιλίες, φωνή, συσκέψεις και agents.',
    loading: 'Φόρτωση επεξεργασίας AI…',
    loadError:
      'Δεν ήταν δυνατή η φόρτωση της επεξεργασίας AI. Ελέγξτε τη σύνδεσή σας και δοκιμάστε ξανά.',
    retry: 'Δοκιμάστε ξανά',
    nowEuTitle: 'Το AI εκτελείται στην ΕΕ',
    nowEuBody:
      'Τα νέα αιτήματα συνομιλίας, φωνής, σύσκεψης και agent χρησιμοποιούν διαδρομές στην ΕΕ και δεν μεταφέρονται ποτέ εκτός ΕΕ.',
    nowGlobalTitle: 'Χρήση των καλύτερων μοντέλων παγκοσμίως',
    nowGlobalBody:
      'Οι αυτόματες συνομιλίες εκτελούνται στην ΕΕ. Άλλες επιλογές μοντέλων και φωνές ενδέχεται να εκτελούνται εκτός ΕΕ.',
    nowScope:
      'Οι συνδεδεμένες υπηρεσίες, οι ιστότοποι και οι δικοί σας agents ακολουθούν τους δικούς τους κανόνες.',
    choicesLabel: 'Πού εκτελείται το AI',
    euTitle: 'Διατήρηση όλης της επεξεργασίας AI στην ΕΕ',
    euBody:
      'Για αυστηρούς κανόνες δεδομένων. Μερικά νεότερα μοντέλα και φωνές δεν είναι ακόμη διαθέσιμα στην ΕΕ.',
    globalTitle: 'Χρήση των καλύτερων μοντέλων παγκοσμίως',
    globalBody: 'Τα ισχυρότερα μοντέλα και οι φωνές, με επεξεργασία στην ΕΕ ή αλλού.',
    current: 'Τρέχουσα επιλογή',
    detailsToggle: 'Τι αλλάζει',
    detailsModel:
      'Η επιλογή Αυτόματα χρησιμοποιεί το {model} στην ΕΕ. Αν δεν είναι διαθέσιμο, το {fallback} απαντά στην ΕΕ.',
    detailsVoice:
      'Οι φωνές της ΕΕ μπορεί να ακούγονται διαφορετικά και να αποκρίνονται λίγο πιο αργά.',
    detailsMeetings:
      'Η αποκατάσταση σύσκεψης χρειάζεται δεύτερη μηχανή ομιλίας στην ΕΕ και διακόπτεται όταν καμία δεν είναι διαθέσιμη.',
    detailsCompaction:
      'Ορισμένα νεότερα μοντέλα δεν μπορούν ακόμη να συμπτύξουν μεγάλες συνομιλίες στην ΕΕ.',
    detailsOthers:
      'Οι ενσωματώσεις, οι δημόσιοι ιστότοποι, οι δικοί σας agents και οι υπηρεσίες λογαριασμού ή χρέωσης ακολουθούν τους κανόνες του εκάστοτε παρόχου.',
    detailsNext:
      'Η αλλαγή εφαρμόζεται σε νέα αιτήματα. Τα αιτήματα που εκτελούνται ήδη ολοκληρώνονται όπως ξεκίνησαν.',
    decisionToEu: 'Όλοι στην εταιρεία {company} θα διατηρήσουν την επεξεργασία AI στην ΕΕ.',
    consequenceToEu: 'Μερικά νεότερα μοντέλα και φωνές δεν θα είναι διαθέσιμα.',
    decisionToGlobal:
      'Όλοι στην εταιρεία {company} θα χρησιμοποιούν τα καλύτερα μοντέλα παγκοσμίως.',
    consequenceToGlobal: 'Τα νέα αιτήματα ενδέχεται να υποβάλλονται σε επεξεργασία εκτός ΕΕ.',
    save: 'Αποθήκευση',
    saving: 'Αποθήκευση…',
    saved: 'Αποθηκεύτηκε',
    adminOnly: 'Μόνο οι Ιδιοκτήτες και οι Διαχειριστές μπορούν να το αλλάξουν.',
    adminOnlyAsk:
      'Μόνο οι Ιδιοκτήτες και οι Διαχειριστές μπορούν να το αλλάξουν. Ρωτήστε τον/την {owner} αν η εταιρεία {company} χρειάζεται διαφορετική επιλογή.',
    currentChoice: 'Τρέχουσα επιλογή',
    conflict: 'Κάποιος άλλαξε αυτήν την επιλογή πριν από λίγο. Φορτώσαμε την πιο πρόσφατη επιλογή.',
    saveError: 'Δεν ήταν δυνατή η αποθήκευση. Δοκιμάστε ξανά.',
  },
  hu: {
    title: 'AI-feldolgozás',
    intro:
      'Válaszd ki, hol fut a Poneva AI-ja mindenki számára a(z) {company} területén: csevegésben, hangalapú kommunikációban, megbeszéléseken és ügynököknél.',
    loading: 'AI-feldolgozás betöltése…',
    loadError:
      'Nem sikerült betölteni az AI-feldolgozási beállításokat. Ellenőrizd a kapcsolatot, majd próbáld újra.',
    retry: 'Újra',
    nowEuTitle: 'Az AI az EU-ban fut',
    nowEuBody:
      'Az új csevegési, hangalapú, megbeszélési és ügynökkérések EU-s útvonalakat használnak, és soha nem kerülnek az EU-n kívülre.',
    nowGlobalTitle: 'A legjobb modellek használata világszerte',
    nowGlobalBody:
      'Az automatikus csevegés az EU-ban fut. Más modellválasztások és hangok az EU-n kívül is futhatnak.',
    nowScope:
      'A csatlakoztatott szolgáltatások, webhelyek és saját ügynökeid a saját szabályaikat követik.',
    choicesLabel: 'Hol fut az AI',
    euTitle: 'Minden AI-feldolgozás maradjon az EU-ban',
    euBody:
      'Szigorú adatkezelési szabályokhoz. Néhány újabb modell és hang még nem érhető el az EU-ban.',
    globalTitle: 'A legjobb modellek használata világszerte',
    globalBody: 'A legerősebb modellek és hangok az EU-ban vagy máshol kerülnek feldolgozásra.',
    current: 'Jelenlegi',
    detailsToggle: 'Mi változik',
    detailsModel:
      'Az automatikus mód a(z) {model} modellt használja az EU-ban. Ha nem érhető el, a(z) {fallback} válaszol az EU-ban.',
    detailsVoice: 'Az EU-s hangok eltérően szólhatnak, és kissé lassabban válaszolhatnak.',
    detailsMeetings:
      'A megbeszélések helyreállításához egy második EU-s beszédmotorra van szükség; ha egyik sem érhető el, a folyamat szünetel.',
    detailsCompaction:
      'Néhány újabb modell egyelőre nem tudja lerövidíteni a hosszú csevegéseket az EU-ban.',
    detailsOthers:
      'Az integrációk, nyilvános webhelyek, saját ügynökeid, valamint a fiók- és számlázási szolgáltatások a saját szolgáltatójuk szabályait követik.',
    detailsNext:
      'A módosítás az új kérésekre vonatkozik. A már futó kérések az eredeti beállítások szerint fejeződnek be.',
    decisionToEu: 'A(z) {company} minden munkatársánál az EU-ban marad az AI-feldolgozás.',
    consequenceToEu: 'Néhány újabb modell és hang nem lesz elérhető.',
    decisionToGlobal:
      'A(z) {company} minden munkatársa a legjobb modelleket használja világszerte.',
    consequenceToGlobal: 'Az új kérések feldolgozása az EU-n kívül is történhet.',
    save: 'Mentés',
    saving: 'Mentés…',
    saved: 'Mentve',
    adminOnly: 'Ezt csak a tulajdonosok és a rendszergazdák módosíthatják.',
    adminOnlyAsk:
      'Ezt csak a tulajdonosok és a rendszergazdák módosíthatják. Kérdezd meg {owner} felhasználót, ha a(z) {company} területén másik beállításra van szükség.',
    currentChoice: 'Jelenlegi választás',
    conflict: 'Valaki az imént módosította ezt. Betöltöttük a legújabb választást.',
    saveError: 'Nem sikerült menteni. Próbáld újra.',
  },
  ga: {
    title: 'Próiseáil AI',
    intro:
      'Roghnaigh cá ritheann AI Poneva do gach duine ag {company}: comhrá, guth, cruinnithe agus gníomhairí.',
    loading: 'AI á luchtú…',
    loadError:
      'Níorbh fhéidir linn próiseáil AI a luchtú. Seiceáil do nasc agus bain triail eile as.',
    retry: 'Bain triail eile as',
    nowEuTitle: 'Ritheann AI san AE',
    nowEuBody:
      'Úsáideann iarratais nua ar chomhrá, guth, cruinnithe agus gníomhairí bealaí san AE agus ní théann siad ar ais chuig bealaí lasmuigh den AE riamh.',
    nowGlobalTitle: 'Na samhlacha is fearr ar fud an domhain á n-úsáid',
    nowGlobalBody:
      'Ritheann comhrá uathoibríoch san AE. D’fhéadfadh roghanna eile samhlacha agus guthanna a bheith á bpróiseáil lasmuigh den AE.',
    nowScope:
      'Leanann seirbhísí nasctha, suíomhanna gréasáin agus do ghníomhairí féin a rialacha féin.',
    choicesLabel: 'Cá ritheann AI',
    euTitle: 'Coinnigh próiseáil uile AI san AE',
    euBody:
      'Do rialacha dochta sonraí. Níl roinnt samhlacha agus guthanna níos nuaí ar fáil san AE go fóill.',
    globalTitle: 'Úsáid na samhlacha is fearr ar fud an domhain',
    globalBody: 'Na samhlacha agus na guthanna is cumhachtaí, próiseáilte san AE nó in áit eile.',
    current: 'Reatha',
    detailsToggle: 'Cad a athraíonn',
    detailsModel:
      'Úsáideann Uathoibríoch {model} san AE. Mura bhfuil sé ar fáil, freagraíonn {fallback} san AE.',
    detailsVoice:
      'D’fhéadfadh guthanna an AE fuaim éagsúil a bheith acu agus freagairt beagán níos moille.',
    detailsMeetings:
      'Teastaíonn an dara hinneall cainte san AE ó dheisiú cruinnithe agus cuirtear ar sos é nuair nach bhfuil aon cheann ar fáil.',
    detailsCompaction:
      'Níl roinnt samhlacha níos nuaí in ann comhráite fada a ghiorrú san AE go fóill.',
    detailsOthers:
      'Leanann comhtháthaithe, suíomhanna gréasáin poiblí, do ghníomhairí féin agus seirbhísí cuntais nó billeála rialacha a soláthraithe féin.',
    detailsNext:
      'Baineann athrú le hiarratais nua. Críochnóidh iarratais atá ar siúl cheana mar a thosaigh siad.',
    decisionToEu: 'Coinneoidh gach duine ag {company} próiseáil AI san AE.',
    consequenceToEu: 'Ní bheidh roinnt samhlacha agus guthanna níos nuaí ar fáil.',
    decisionToGlobal: 'Úsáidfidh gach duine ag {company} na samhlacha is fearr ar fud an domhain.',
    consequenceToGlobal: 'D’fhéadfaí iarratais nua a phróiseáil lasmuigh den AE.',
    save: 'Sábháil',
    saving: 'Á shábháil…',
    saved: 'Sábháilte',
    adminOnly: 'Ní féidir ach le hÚinéirí agus Riarthóirí é seo a athrú.',
    adminOnlyAsk:
      'Ní féidir ach le hÚinéirí agus Riarthóirí é seo a athrú. Fiafraigh de {owner} an bhfuil rogha eile de dhíth ar {company}.',
    currentChoice: 'Rogha reatha',
    conflict:
      'D’athraigh duine éigin é seo tamall beag ó shin. Tá an rogha is déanaí luchtaithe againn.',
    saveError: 'Níorbh fhéidir linn é seo a shábháil. Bain triail eile as.',
  },
  it: {
    title: 'Elaborazione IA',
    intro:
      'Scegli dove viene eseguita l’IA di Poneva per tutti gli utenti di {company}: chat, voce, riunioni e agenti.',
    loading: 'Caricamento dell’elaborazione IA…',
    loadError:
      'Non è stato possibile caricare l’elaborazione IA. Controlla la connessione e riprova.',
    retry: 'Riprova',
    nowEuTitle: 'L’IA viene eseguita nell’UE',
    nowEuBody:
      'Le nuove richieste di chat, voce, riunioni e agenti usano percorsi nell’UE e non vengono mai indirizzate fuori dall’UE.',
    nowGlobalTitle: 'Utilizzo dei modelli migliori in tutto il mondo',
    nowGlobalBody:
      'La chat automatica viene eseguita nell’UE. Altre scelte di modelli e voci potrebbero essere elaborate fuori dall’UE.',
    nowScope: 'I servizi connessi, i siti web e i tuoi agenti seguono le proprie regole.',
    choicesLabel: 'Dove viene eseguita l’IA',
    euTitle: 'Mantieni tutta l’elaborazione IA nell’UE',
    euBody:
      'Per rispettare regole rigorose sui dati. Alcuni modelli e voci più recenti non sono ancora disponibili nell’UE.',
    globalTitle: 'Usa i modelli migliori in tutto il mondo',
    globalBody: 'I modelli e le voci più avanzati, elaborati nell’UE o altrove.',
    current: 'Attuale',
    detailsToggle: 'Cosa cambia',
    detailsModel:
      'La modalità automatica usa {model} nell’UE. Se non è disponibile, {fallback} risponde nell’UE.',
    detailsVoice:
      'Le voci dell’UE possono avere un suono diverso e rispondere un po’ più lentamente.',
    detailsMeetings:
      'La correzione delle riunioni richiede un secondo motore vocale dell’UE e si interrompe quando non è disponibile.',
    detailsCompaction:
      'Alcuni modelli più recenti non possono ancora accorciare le chat lunghe nell’UE.',
    detailsOthers:
      'Le integrazioni, i siti web pubblici, i tuoi agenti e i servizi dell’account o di fatturazione seguono le regole dei rispettivi fornitori.',
    detailsNext:
      'La modifica si applica alle nuove richieste. Le richieste già in corso terminano come sono iniziate.',
    decisionToEu: 'Tutti gli utenti di {company} manterranno l’elaborazione IA nell’UE.',
    consequenceToEu: 'Alcuni modelli e voci più recenti non saranno disponibili.',
    decisionToGlobal:
      'Tutti gli utenti di {company} useranno i modelli migliori in tutto il mondo.',
    consequenceToGlobal: 'Le nuove richieste potrebbero essere elaborate fuori dall’UE.',
    save: 'Salva',
    saving: 'Salvataggio…',
    saved: 'Salvato',
    adminOnly: 'Solo i Proprietari e gli Amministratori possono modificare questa impostazione.',
    adminOnlyAsk:
      'Solo i Proprietari e gli Amministratori possono modificare questa impostazione. Chiedi a {owner} se {company} necessita di una scelta diversa.',
    currentChoice: 'Scelta attuale',
    conflict:
      'Qualcuno ha modificato questa impostazione poco fa. Abbiamo caricato la scelta più recente.',
    saveError: 'Non è stato possibile salvare. Riprova.',
  },
  lv: {
    title: 'MI apstrāde',
    intro:
      'Izvēlieties, kur darbojas Poneva MI visiem uzņēmumā {company}: tērzēšanai, balsij, sapulcēm un aģentiem.',
    loading: 'Notiek MI apstrādes ielāde…',
    loadError:
      'Neizdevās ielādēt MI apstrādes iestatījumus. Pārbaudiet savienojumu un mēģiniet vēlreiz.',
    retry: 'Mēģināt vēlreiz',
    nowEuTitle: 'MI darbojas ES',
    nowEuBody:
      'Jauni tērzēšanas, balss, sapulču un aģentu pieprasījumi izmanto ES maršrutus un nekad netiek novirzīti ārpus ES.',
    nowGlobalTitle: 'Tiek izmantoti labākie modeļi visā pasaulē',
    nowGlobalBody:
      'Automātiskā tērzēšana darbojas ES. Citas modeļu izvēles un balsis var darboties ārpus ES.',
    nowScope: 'Savienotie pakalpojumi, tīmekļa vietnes un jūsu aģenti ievēro savus noteikumus.',
    choicesLabel: 'Kur darbojas MI',
    euTitle: 'Saglabāt visu MI apstrādi ES',
    euBody:
      'Stingrām datu aizsardzības prasībām. Daži jaunāki modeļi un balsis ES vēl nav pieejami.',
    globalTitle: 'Izmantot labākos modeļus visā pasaulē',
    globalBody: 'Jaudīgākie modeļi un balsis tiek apstrādāti ES vai citur.',
    current: 'Pašreizējais',
    detailsToggle: 'Kas mainās',
    detailsModel:
      'Automātiskais režīms ES izmanto {model}. Ja tas nav pieejams, atbildi ES sniedz {fallback}.',
    detailsVoice: 'ES balsis var skanēt citādi un atbildēt nedaudz lēnāk.',
    detailsMeetings:
      'Sapulču labošanai ir nepieciešams otrs ES runas dzinis; ja tas nav pieejams, labošana tiek apturēta.',
    detailsCompaction: 'Daži jaunāki modeļi ES vēl nevar saīsināt garas tērzēšanas sarunas.',
    detailsOthers:
      'Integrācijas, publiskās tīmekļa vietnes, jūsu aģenti un konta vai norēķinu pakalpojumi ievēro sava pakalpojumu sniedzēja noteikumus.',
    detailsNext:
      'Izmaiņa attiecas uz jauniem pieprasījumiem. Jau izpildē esošie pieprasījumi tiek pabeigti, izmantojot iepriekšējos iestatījumus.',
    decisionToEu: 'Visi uzņēmuma {company} lietotāji turpinās izmantot MI apstrādi ES.',
    consequenceToEu: 'Daži jaunāki modeļi un balsis nebūs pieejami.',
    decisionToGlobal: 'Visi uzņēmuma {company} lietotāji izmantos labākos modeļus visā pasaulē.',
    consequenceToGlobal: 'Jauni pieprasījumi var tikt apstrādāti ārpus ES.',
    save: 'Saglabāt',
    saving: 'Notiek saglabāšana…',
    saved: 'Saglabāts',
    adminOnly: 'Tikai īpašnieki un administratori var to mainīt.',
    adminOnlyAsk:
      'Tikai īpašnieki un administratori var to mainīt. Ja uzņēmumam {company} nepieciešama cita izvēle, jautājiet {owner}.',
    currentChoice: 'Pašreizējā izvēle',
    conflict: 'Kāds to tikko mainīja. Esam ielādējuši jaunāko izvēli.',
    saveError: 'Neizdevās to saglabāt. Mēģiniet vēlreiz.',
  },
  lt: {
    title: 'AI apdorojimas',
    intro:
      'Pasirinkite, kur visiems naudotojams įmonėje {company} veikia Poneva AI: pokalbiuose, balsu, susitikimuose ir agentuose.',
    loading: 'Įkeliamas AI apdorojimas…',
    loadError: 'Nepavyko įkelti AI apdorojimo. Patikrinkite ryšį ir bandykite dar kartą.',
    retry: 'Bandyti dar kartą',
    nowEuTitle: 'AI veikia ES',
    nowEuBody:
      'Naujoms pokalbių, balso, susitikimų ir agentų užklausoms naudojami ES maršrutai ir jos niekada nenukreipiamos už ES ribų.',
    nowGlobalTitle: 'Naudojami geriausi modeliai visame pasaulyje',
    nowGlobalBody:
      'Automatiniai pokalbiai vykdomi ES. Kiti modelių pasirinkimai ir balsai gali veikti už ES ribų.',
    nowScope: 'Prijungtos paslaugos, svetainės ir jūsų agentai vadovaujasi savo taisyklėmis.',
    choicesLabel: 'Kur veikia AI',
    euTitle: 'Visą AI apdorojimą vykdyti ES',
    euBody:
      'Tinka taikant griežtus duomenų reikalavimus. Kai kurie naujesni modeliai ir balsai ES dar nepasiekiami.',
    globalTitle: 'Naudoti geriausius modelius visame pasaulyje',
    globalBody: 'Galingiausi modeliai ir balsai apdorojami ES arba kitur.',
    current: 'Dabartinis',
    detailsToggle: 'Kas keičiasi',
    detailsModel:
      'Automatiniam režimui ES naudojamas {model}. Jei jis nepasiekiamas, atsakymą ES pateikia {fallback}.',
    detailsVoice: 'ES balsai gali skambėti kitaip ir atsakyti šiek tiek lėčiau.',
    detailsMeetings:
      'Susitikimų taisymui reikia antro ES kalbos variklio; jei jo nėra, ši funkcija pristabdoma.',
    detailsCompaction: 'Kai kurie naujesni modeliai kol kas negali sutrumpinti ilgų pokalbių ES.',
    detailsOthers:
      'Integracijos, viešosios svetainės, jūsų agentai ir paskyros ar atsiskaitymo paslaugos vadovaujasi savo teikėjų taisyklėmis.',
    detailsNext:
      'Pakeitimas taikomas naujoms užklausoms. Jau vykdomos užklausos užbaigiamos taip, kaip buvo pradėtos.',
    decisionToEu: 'Visų įmonės {company} naudotojų AI apdorojimas ir toliau bus vykdomas ES.',
    consequenceToEu: 'Kai kurie naujesni modeliai ir balsai nebus pasiekiami.',
    decisionToGlobal:
      'Visi įmonės {company} naudotojai naudos geriausius modelius visame pasaulyje.',
    consequenceToGlobal: 'Naujos užklausos gali būti apdorojamos už ES ribų.',
    save: 'Išsaugoti',
    saving: 'Išsaugoma…',
    saved: 'Išsaugota',
    adminOnly: 'Tai pakeisti gali tik savininkai ir administratoriai.',
    adminOnlyAsk:
      'Tai pakeisti gali tik savininkai ir administratoriai. Jei įmonei {company} reikia kito pasirinkimo, kreipkitės į {owner}.',
    currentChoice: 'Dabartinis pasirinkimas',
    conflict: 'Kažkas ką tik tai pakeitė. Įkėlėme naujausią pasirinkimą.',
    saveError: 'Nepavyko išsaugoti. Bandykite dar kartą.',
  },
  mt: {
    title: 'Ipproċessar tal-AI',
    intro:
      'Agħżel fejn taħdem l-AI ta’ Poneva għal kulħadd f’{company}: chat, vuċi, laqgħat u aġenti.',
    loading: 'Qed jitgħabba l-ipproċessar tal-AI…',
    loadError:
      'Ma stajniex intellgħu l-ipproċessar tal-AI. Iċċekkja l-konnessjoni tiegħek u erġa’ pprova.',
    retry: 'Erġa’ pprova',
    nowEuTitle: 'L-AI taħdem fl-UE',
    nowEuBody:
      'Talbiet ġodda għal chat, vuċi, laqgħat u aġenti jużaw rotot tal-UE u qatt ma jaqilbu għal barra mill-UE.',
    nowGlobalTitle: 'Qed jintużaw l-aħjar mudelli madwar id-dinja',
    nowGlobalBody:
      'Il-chat awtomatiku jaħdem fl-UE. Għażliet oħra ta’ mudelli u vuċijiet jistgħu jaħdmu barra mill-UE.',
    nowScope: 'Is-servizzi konnessi, is-siti web u l-aġenti tiegħek isegwu r-regoli tagħhom stess.',
    choicesLabel: 'Fejn taħdem l-AI',
    euTitle: 'Żomm l-ipproċessar kollu tal-AI fl-UE',
    euBody:
      'Għal regoli stretti dwar id-dejta. Xi mudelli u vuċijiet aktar ġodda għadhom mhumiex disponibbli fl-UE.',
    globalTitle: 'Uża l-aħjar mudelli madwar id-dinja',
    globalBody: 'L-aktar mudelli u vuċijiet b’saħħithom, ipproċessati fl-UE jew f’post ieħor.',
    current: 'Kurrenti',
    detailsToggle: 'X’jinbidel',
    detailsModel: 'Auto juża {model} fl-UE. Jekk ma jkunx disponibbli, {fallback} iwieġeb fl-UE.',
    detailsVoice: 'Il-vuċijiet tal-UE jistgħu jinstemgħu differenti u jwieġbu ftit aktar bil-mod.',
    detailsMeetings:
      'It-tiswija tal-laqgħat teħtieġ magna oħra tad-diskors fl-UE u tieqaf meta ma tkun disponibbli l-ebda waħda.',
    detailsCompaction: 'Xi mudelli aktar ġodda għadhom ma jistgħux iqassru chats twal fl-UE.',
    detailsOthers:
      'L-integrazzjonijiet, is-siti web pubbliċi, l-aġenti tiegħek, u s-servizzi tal-kont jew tal-kontijiet isegwu r-regoli tal-fornitur tagħhom stess.',
    detailsNext: 'Bidla tapplika għal talbiet ġodda. Talbiet li diġà għaddejjin jintemmu kif bdew.',
    decisionToEu: 'Kulħadd f’{company} se jżomm l-ipproċessar tal-AI fl-UE.',
    consequenceToEu: 'Xi mudelli u vuċijiet aktar ġodda mhux se jkunu disponibbli.',
    decisionToGlobal: 'Kulħadd f’{company} se juża l-aħjar mudelli madwar id-dinja.',
    consequenceToGlobal: 'Talbiet ġodda jistgħu jiġu pproċessati barra mill-UE.',
    save: 'Issejvja',
    saving: 'Qed jiġi ssejvjat…',
    saved: 'Issejvjat',
    adminOnly: 'Is-Sidien u l-Amministraturi biss jistgħu jibdlu dan.',
    adminOnlyAsk:
      'Is-Sidien u l-Amministraturi biss jistgħu jibdlu dan. Staqsi lil {owner} jekk {company} teħtieġx għażla differenti.',
    currentChoice: 'Għażla kurrenti',
    conflict: 'Xi ħadd biddel dan ftit ilu. Tgħabbiet l-aħħar għażla.',
    saveError: 'Ma stajniex insalvaw dan. Erġa’ pprova.',
  },
  pl: {
    title: 'Przetwarzanie AI',
    intro:
      'Wybierz, gdzie działa AI Poneva dla wszystkich w {company}: czaty, głos, spotkania i agenci.',
    loading: 'Ładowanie przetwarzania AI…',
    loadError:
      'Nie udało się załadować ustawień przetwarzania AI. Sprawdź połączenie i spróbuj ponownie.',
    retry: 'Spróbuj ponownie',
    nowEuTitle: 'AI działa w UE',
    nowEuBody:
      'Nowe żądania dotyczące czatów, głosu, spotkań i agentów korzystają z tras w UE i nigdy nie są kierowane poza UE.',
    nowGlobalTitle: 'Korzystanie z najlepszych modeli na całym świecie',
    nowGlobalBody:
      'Automatyczny czat działa w UE. Inne wybrane modele i głosy mogą działać poza UE.',
    nowScope: 'Połączone usługi, strony internetowe i własni agenci podlegają własnym zasadom.',
    choicesLabel: 'Gdzie działa AI',
    euTitle: 'Zachowaj całe przetwarzanie AI w UE',
    euBody:
      'Dla rygorystycznych zasad dotyczących danych. Niektóre nowsze modele i głosy nie są jeszcze dostępne w UE.',
    globalTitle: 'Korzystaj z najlepszych modeli na całym świecie',
    globalBody: 'Najlepsze modele i głosy, przetwarzane w UE lub w innych miejscach.',
    current: 'Bieżące',
    detailsToggle: 'Co się zmienia',
    detailsModel:
      'Automatycznie używany jest {model} w UE. Jeśli jest niedostępny, {fallback} odpowiada w UE.',
    detailsVoice: 'Głosy z UE mogą brzmieć inaczej i odpowiadać nieco wolniej.',
    detailsMeetings:
      'Naprawa spotkań wymaga drugiego silnika mowy w UE i zostaje wstrzymana, gdy żaden nie jest dostępny.',
    detailsCompaction: 'Niektóre nowsze modele nie mogą jeszcze skracać długich czatów w UE.',
    detailsOthers:
      'Integracje, publiczne strony internetowe, własni agenci oraz usługi konta i rozliczeń podlegają zasadom odpowiednich dostawców.',
    detailsNext:
      'Zmiana dotyczy nowych żądań. Żądania już w toku zostaną ukończone zgodnie z dotychczasowymi ustawieniami.',
    decisionToEu: 'Wszyscy w {company} będą nadal korzystać z przetwarzania AI w UE.',
    consequenceToEu: 'Niektóre nowsze modele i głosy nie będą dostępne.',
    decisionToGlobal: 'Wszyscy w {company} będą korzystać z najlepszych modeli na całym świecie.',
    consequenceToGlobal: 'Nowe żądania mogą być przetwarzane poza UE.',
    save: 'Zapisz',
    saving: 'Zapisywanie…',
    saved: 'Zapisano',
    adminOnly: 'Tylko właściciele i administratorzy mogą to zmienić.',
    adminOnlyAsk:
      'Tylko właściciele i administratorzy mogą to zmienić. Zapytaj {owner}, jeśli {company} potrzebuje innego wyboru.',
    currentChoice: 'Bieżący wybór',
    conflict: 'Ktoś przed chwilą zmienił to ustawienie. Załadowaliśmy najnowszy wybór.',
    saveError: 'Nie udało się tego zapisać. Spróbuj ponownie.',
  },
  pt: {
    title: 'Processamento de IA',
    intro:
      'Escolha onde a IA da Poneva é executada para todos em {company}: chat, voz, reuniões e agentes.',
    loading: 'Carregando o processamento de IA…',
    loadError:
      'Não foi possível carregar o processamento de IA. Verifique sua conexão e tente novamente.',
    retry: 'Tentar novamente',
    nowEuTitle: 'A IA é executada na UE',
    nowEuBody:
      'Novas solicitações de chat, voz, reuniões e agentes usam rotas da UE e nunca são redirecionadas para fora da UE.',
    nowGlobalTitle: 'Usando os melhores modelos do mundo',
    nowGlobalBody:
      'O chat automático é executado na UE. Outras opções de modelos e vozes podem ser executadas fora da UE.',
    nowScope: 'Serviços conectados, sites e seus próprios agentes seguem suas próprias regras.',
    choicesLabel: 'Onde a IA é executada',
    euTitle: 'Manter todo o processamento de IA na UE',
    euBody:
      'Para regras rigorosas de dados. Alguns modelos e vozes mais recentes ainda não estão disponíveis na UE.',
    globalTitle: 'Usar os melhores modelos do mundo',
    globalBody: 'Os modelos e vozes mais avançados, processados na UE ou em outros locais.',
    current: 'Atual',
    detailsToggle: 'O que muda',
    detailsModel:
      'O modo automático usa {model} na UE. Se ele não estiver disponível, {fallback} responde na UE.',
    detailsVoice: 'As vozes da UE podem soar diferentes e responder um pouco mais devagar.',
    detailsMeetings:
      'O reparo de reuniões precisa de um segundo mecanismo de voz da UE e é pausado quando nenhum está disponível.',
    detailsCompaction:
      'Alguns modelos mais recentes ainda não conseguem encurtar chats longos na UE.',
    detailsOthers:
      'Integrações, sites públicos, seus próprios agentes e serviços de conta ou cobrança seguem as regras de seus respectivos provedores.',
    detailsNext:
      'A alteração se aplica a novas solicitações. As solicitações já em execução serão concluídas como começaram.',
    decisionToEu: 'Todos em {company} manterão o processamento de IA na UE.',
    consequenceToEu: 'Alguns modelos e vozes mais recentes não estarão disponíveis.',
    decisionToGlobal: 'Todos em {company} usarão os melhores modelos do mundo.',
    consequenceToGlobal: 'Novas solicitações poderão ser processadas fora da UE.',
    save: 'Salvar',
    saving: 'Salvando…',
    saved: 'Salvo',
    adminOnly: 'Somente Proprietários e Administradores podem alterar isso.',
    adminOnlyAsk:
      'Somente Proprietários e Administradores podem alterar isso. Pergunte a {owner} se {company} precisa de uma opção diferente.',
    currentChoice: 'Opção atual',
    conflict: 'Alguém alterou isso há pouco. Carregamos a opção mais recente.',
    saveError: 'Não foi possível salvar. Tente novamente.',
  },
  ro: {
    title: 'Procesarea AI',
    intro:
      'Alege unde rulează AI-ul Poneva pentru toți membrii companiei {company}: chat, voce, întâlniri și agenți.',
    loading: 'Se încarcă procesarea AI…',
    loadError: 'Nu am putut încărca procesarea AI. Verifică conexiunea și încearcă din nou.',
    retry: 'Încearcă din nou',
    nowEuTitle: 'AI rulează în UE',
    nowEuBody:
      'Solicitările noi de chat, voce, întâlniri și agenți folosesc rute din UE și nu sunt redirecționate niciodată în afara UE.',
    nowGlobalTitle: 'Folosirea celor mai bune modele din întreaga lume',
    nowGlobalBody:
      'Chatul automat rulează în UE. Alte opțiuni de modele și vocile pot rula în afara UE.',
    nowScope: 'Serviciile conectate, site-urile web și agenții proprii urmează propriile reguli.',
    choicesLabel: 'Unde rulează AI',
    euTitle: 'Păstrează toată procesarea AI în UE',
    euBody:
      'Pentru reguli stricte privind datele. Câteva modele și voci mai noi nu sunt încă disponibile în UE.',
    globalTitle: 'Folosește cele mai bune modele din întreaga lume',
    globalBody: 'Cele mai puternice modele și voci, procesate în UE sau în alte regiuni.',
    current: 'Actual',
    detailsToggle: 'Ce se schimbă',
    detailsModel:
      'Automat folosește {model} în UE. Dacă nu este disponibil, {fallback} răspunde în UE.',
    detailsVoice: 'Vocile din UE pot suna diferit și pot răspunde puțin mai lent.',
    detailsMeetings:
      'Remedierea întâlnirilor necesită un al doilea motor vocal din UE și se oprește temporar când niciunul nu este disponibil.',
    detailsCompaction: 'Unele modele mai noi nu pot scurta încă discuțiile lungi în UE.',
    detailsOthers:
      'Integrările, site-urile web publice, agenții proprii și serviciile pentru cont sau facturare urmează regulile propriului furnizor.',
    detailsNext:
      'Schimbarea se aplică solicitărilor noi. Solicitările deja în curs se finalizează în condițiile inițiale.',
    decisionToEu: 'Toți membrii companiei {company} vor păstra procesarea AI în UE.',
    consequenceToEu: 'Câteva modele și voci mai noi nu vor fi disponibile.',
    decisionToGlobal:
      'Toți membrii companiei {company} vor folosi cele mai bune modele din întreaga lume.',
    consequenceToGlobal: 'Solicitările noi pot fi procesate în afara UE.',
    save: 'Salvează',
    saving: 'Se salvează…',
    saved: 'Salvat',
    adminOnly: 'Doar proprietarii și administratorii pot modifica această setare.',
    adminOnlyAsk:
      'Doar proprietarii și administratorii pot modifica această setare. Întreabă-l pe {owner} dacă {company} are nevoie de o altă opțiune.',
    currentChoice: 'Opțiunea actuală',
    conflict:
      'Cineva a modificat această setare cu puțin timp în urmă. Am încărcat cea mai recentă opțiune.',
    saveError: 'Nu am putut salva modificarea. Încearcă din nou.',
  },
  sk: {
    title: 'Spracovanie AI',
    intro:
      'Vyberte, kde bude AI od Poneva fungovať pre všetkých v {company}: chat, hlas, schôdze a agenti.',
    loading: 'Načítava sa spracovanie AI…',
    loadError: 'Spracovanie AI sa nepodarilo načítať. Skontrolujte pripojenie a skúste to znova.',
    retry: 'Skúsiť znova',
    nowEuTitle: 'AI funguje v EÚ',
    nowEuBody:
      'Nové požiadavky chatu, hlasu, schôdzí a agentov používajú trasy v EÚ a nikdy sa nepresmerujú mimo EÚ.',
    nowGlobalTitle: 'Používajú sa najlepšie modely na svete',
    nowGlobalBody:
      'Automatický chat funguje v EÚ. Ostatné voľby modelov a hlasy môžu fungovať mimo EÚ.',
    nowScope: 'Pripojené služby, webové stránky a vaši agenti sa riadia vlastnými pravidlami.',
    choicesLabel: 'Kde funguje AI',
    euTitle: 'Spracovávať všetku AI v EÚ',
    euBody:
      'Pre prísne pravidlá ochrany údajov. Niekoľko novších modelov a hlasov zatiaľ nie je v EÚ dostupných.',
    globalTitle: 'Používať najlepšie modely na svete',
    globalBody: 'Najvýkonnejšie modely a hlasy sa spracúvajú v EÚ alebo inde.',
    current: 'Aktuálne',
    detailsToggle: 'Čo sa zmení',
    detailsModel:
      'Automatický režim používa {model} v EÚ. Ak nie je dostupný, {fallback} odpovedá v EÚ.',
    detailsVoice: 'Hlasy v EÚ môžu znieť odlišne a odpovedať o trochu pomalšie.',
    detailsMeetings:
      'Oprava schôdzí potrebuje druhý hlasový engine v EÚ a pozastaví sa, keď nie je dostupný žiadny.',
    detailsCompaction: 'Niektoré novšie modely zatiaľ nedokážu v EÚ skrátiť dlhé chaty.',
    detailsOthers:
      'Integrácie, verejné webové stránky, vaši agenti a služby účtu či fakturácie sa riadia pravidlami vlastných poskytovateľov.',
    detailsNext:
      'Zmena sa uplatní na nové požiadavky. Už prebiehajúce požiadavky sa dokončia podľa pôvodných nastavení.',
    decisionToEu: 'Všetci v {company} budú naďalej spracúvať AI v EÚ.',
    consequenceToEu: 'Niekoľko novších modelov a hlasov nebude dostupných.',
    decisionToGlobal: 'Všetci v {company} budú používať najlepšie modely na svete.',
    consequenceToGlobal: 'Nové požiadavky sa môžu spracúvať mimo EÚ.',
    save: 'Uložiť',
    saving: 'Ukladá sa…',
    saved: 'Uložené',
    adminOnly: 'Toto môžu meniť iba vlastníci a správcovia.',
    adminOnlyAsk:
      'Toto môžu meniť iba vlastníci a správcovia. Ak {company} potrebuje inú voľbu, obráťte sa na {owner}.',
    currentChoice: 'Aktuálna voľba',
    conflict: 'Niekto to pred chvíľou zmenil. Načítali sme najnovšiu voľbu.',
    saveError: 'Nepodarilo sa to uložiť. Skúste to znova.',
  },
  sl: {
    title: 'Obdelava z umetno inteligenco',
    intro:
      'Izberite, kje za vse pri {company} poteka obdelava z umetno inteligenco: klepet, glas, sestanki in agenti.',
    loading: 'Nalaganje obdelave z umetno inteligenco…',
    loadError:
      'Obdelave z umetno inteligenco ni bilo mogoče naložiti. Preverite povezavo in poskusite znova.',
    retry: 'Poskusite znova',
    nowEuTitle: 'Obdelava z umetno inteligenco poteka v EU',
    nowEuBody:
      'Nove zahteve za klepet, glas, sestanke in agente uporabljajo poti v EU in se nikoli ne preusmerijo zunaj EU.',
    nowGlobalTitle: 'Uporaba najboljših modelov po vsem svetu',
    nowGlobalBody:
      'Samodejni klepet poteka v EU. Druge izbire modelov in glasovi se lahko izvajajo zunaj EU.',
    nowScope: 'Povezane storitve, spletna mesta in vaši agenti upoštevajo lastna pravila.',
    choicesLabel: 'Kje poteka obdelava z umetno inteligenco',
    euTitle: 'Vsa obdelava z umetno inteligenco naj ostane v EU',
    euBody:
      'Za stroga pravila glede podatkov. Nekaj novejših modelov in glasov v EU še ni na voljo.',
    globalTitle: 'Uporaba najboljših modelov po vsem svetu',
    globalBody: 'Najzmogljivejši modeli in glasovi, obdelani v EU ali drugod.',
    current: 'Trenutno',
    detailsToggle: 'Kaj se spremeni',
    detailsModel:
      'Samodejni način v EU uporablja {model}. Če ta ni na voljo, v EU odgovarja {fallback}.',
    detailsVoice: 'Glasovi EU lahko zvenijo drugače in se odzivajo nekoliko počasneje.',
    detailsMeetings:
      'Popravljanje sestankov potrebuje drugi govorni mehanizem v EU in se ustavi, ko ta ni na voljo.',
    detailsCompaction: 'Nekateri novejši modeli v EU še ne morejo skrajšati dolgih klepetov.',
    detailsOthers:
      'Integracije, javna spletna mesta, vaši agenti ter storitve za račun in obračunavanje upoštevajo pravila svojih ponudnikov.',
    detailsNext:
      'Sprememba velja za nove zahteve. Zahteve, ki že potekajo, se dokončajo tako, kot so se začele.',
    decisionToEu: 'Vsi pri {company} bodo ohranili obdelavo z umetno inteligenco v EU.',
    consequenceToEu: 'Nekaj novejših modelov in glasov ne bo na voljo.',
    decisionToGlobal: 'Vsi pri {company} bodo uporabljali najboljše modele po vsem svetu.',
    consequenceToGlobal: 'Nove zahteve se lahko obdelajo zunaj EU.',
    save: 'Shrani',
    saving: 'Shranjevanje…',
    saved: 'Shranjeno',
    adminOnly: 'To lahko spremenijo samo lastniki in skrbniki.',
    adminOnlyAsk:
      'To lahko spremenijo samo lastniki in skrbniki. Če {company} potrebuje drugačno izbiro, vprašajte {owner}.',
    currentChoice: 'Trenutna izbira',
    conflict: 'Nekdo je to pravkar spremenil. Naložili smo najnovejšo izbiro.',
    saveError: 'Tega ni bilo mogoče shraniti. Poskusite znova.',
  },
  es: {
    title: 'Procesamiento de IA',
    intro:
      'Elige dónde se ejecuta la IA de Poneva para todos en {company}: chat, voz, reuniones y agentes.',
    loading: 'Cargando el procesamiento de IA…',
    loadError:
      'No pudimos cargar el procesamiento de IA. Comprueba tu conexión e inténtalo de nuevo.',
    retry: 'Intentar de nuevo',
    nowEuTitle: 'La IA se ejecuta en la UE',
    nowEuBody:
      'Las nuevas solicitudes de chat, voz, reuniones y agentes usan rutas de la UE y nunca recurren a servicios fuera de la UE.',
    nowGlobalTitle: 'Usando los mejores modelos del mundo',
    nowGlobalBody:
      'El chat automático se ejecuta en la UE. Otras opciones de modelos y voces pueden ejecutarse fuera de la UE.',
    nowScope:
      'Los servicios conectados, los sitios web y tus propios agentes siguen sus propias reglas.',
    choicesLabel: 'Dónde se ejecuta la IA',
    euTitle: 'Mantener todo el procesamiento de IA en la UE',
    euBody:
      'Para cumplir normas estrictas de protección de datos. Algunos modelos y voces más nuevos aún no están disponibles en la UE.',
    globalTitle: 'Usar los mejores modelos del mundo',
    globalBody: 'Los modelos y voces más avanzados, procesados en la UE o en otros lugares.',
    current: 'Actual',
    detailsToggle: 'Qué cambia',
    detailsModel: 'Auto usa {model} en la UE. Si no está disponible, {fallback} responde en la UE.',
    detailsVoice: 'Las voces de la UE pueden sonar diferentes y responder un poco más despacio.',
    detailsMeetings:
      'La reparación de reuniones necesita un segundo motor de voz de la UE y se pausa cuando ninguno está disponible.',
    detailsCompaction:
      'Algunos modelos más nuevos todavía no pueden acortar chats largos en la UE.',
    detailsOthers:
      'Las integraciones, los sitios web públicos, tus propios agentes y los servicios de cuenta o facturación siguen las reglas de sus respectivos proveedores.',
    detailsNext:
      'El cambio se aplica a las nuevas solicitudes. Las solicitudes que ya están en curso terminan como empezaron.',
    decisionToEu: 'Todos en {company} mantendrán el procesamiento de IA en la UE.',
    consequenceToEu: 'Algunos modelos y voces más nuevos no estarán disponibles.',
    decisionToGlobal: 'Todos en {company} usarán los mejores modelos del mundo.',
    consequenceToGlobal: 'Las nuevas solicitudes pueden procesarse fuera de la UE.',
    save: 'Guardar',
    saving: 'Guardando…',
    saved: 'Guardado',
    adminOnly: 'Solo los propietarios y administradores pueden cambiar esto.',
    adminOnlyAsk:
      'Solo los propietarios y administradores pueden cambiar esto. Pregunta a {owner} si {company} necesita una opción diferente.',
    currentChoice: 'Opción actual',
    conflict: 'Alguien cambió esto hace un momento. Hemos cargado la opción más reciente.',
    saveError: 'No pudimos guardar esto. Inténtalo de nuevo.',
  },
  sv: {
    title: 'AI-bearbetning',
    intro: 'Välj var Ponevas AI körs för alla på {company}: chatt, röst, möten och agenter.',
    loading: 'Läser in AI-bearbetning…',
    loadError:
      'Det gick inte att läsa in AI-bearbetningen. Kontrollera anslutningen och försök igen.',
    retry: 'Försök igen',
    nowEuTitle: 'AI körs i EU',
    nowEuBody:
      'Nya chatt-, röst-, mötes- och agentförfrågningar använder EU-rutter och faller aldrig tillbaka på rutter utanför EU.',
    nowGlobalTitle: 'De bästa modellerna används över hela världen',
    nowGlobalBody: 'Automatisk chatt körs i EU. Andra modellval och röster kan köras utanför EU.',
    nowScope: 'Anslutna tjänster, webbplatser och dina egna agenter följer sina egna regler.',
    choicesLabel: 'Var AI körs',
    euTitle: 'Behåll all AI-bearbetning i EU',
    euBody:
      'För strikta dataskyddsregler. Några nyare modeller och röster är ännu inte tillgängliga i EU.',
    globalTitle: 'Använd de bästa modellerna över hela världen',
    globalBody: 'De bästa modellerna och rösterna bearbetas i EU eller någon annanstans.',
    current: 'Aktuellt',
    detailsToggle: 'Vad ändras',
    detailsModel:
      'Automatiskt använder {model} i EU. Om den inte är tillgänglig svarar {fallback} i EU.',
    detailsVoice: 'EU-röster kan låta annorlunda och svara något långsammare.',
    detailsMeetings:
      'Reparation av möten kräver en andra EU-baserad talmotor och pausas när ingen är tillgänglig.',
    detailsCompaction: 'Vissa nyare modeller kan ännu inte korta ned långa chattar i EU.',
    detailsOthers:
      'Integrationer, offentliga webbplatser, dina egna agenter samt konto- och faktureringstjänster följer respektive leverantörs regler.',
    detailsNext:
      'En ändring gäller för nya förfrågningar. Förfrågningar som redan körs slutförs enligt de tidigare inställningarna.',
    decisionToEu: 'Alla på {company} kommer att behålla AI-bearbetningen i EU.',
    consequenceToEu: 'Några nyare modeller och röster kommer inte att vara tillgängliga.',
    decisionToGlobal: 'Alla på {company} kommer att använda de bästa modellerna över hela världen.',
    consequenceToGlobal: 'Nya förfrågningar kan bearbetas utanför EU.',
    save: 'Spara',
    saving: 'Sparar…',
    saved: 'Sparat',
    adminOnly: 'Endast ägare och administratörer kan ändra detta.',
    adminOnlyAsk:
      'Endast ägare och administratörer kan ändra detta. Fråga {owner} om {company} behöver ett annat val.',
    currentChoice: 'Aktuellt val',
    conflict: 'Någon ändrade detta nyss. Vi har läst in det senaste valet.',
    saveError: 'Det gick inte att spara detta. Försök igen.',
  },
} as const;

export default companyProcessingCopy satisfies NamespaceCatalog<
  keyof typeof companyProcessingCopy.en
>;
